/**
 * pii-untagged lint rule — surfaces fields whose name suggests PII
 * (email, ssn, dob, phone, address) but lack a `pii:*` tag on the entity.
 * Common cause: a sensitive field was added without updating the data-
 * classification tags — a compliance gap.
 *
 * NOTE: Distinction between piiCategory and pii:* tags
 * ─────────────────────────────────────────────────────
 *
 * `piiCategory` (Entity.piiCategory)
 *   - Structured enum: 'DIRECT' | 'QUASI' | 'FINANCIAL' | 'BEHAVIORAL' | 'MINOR'
 *   - Entity-level classification — determines what *kind* of personal data the
 *     entity carries (e.g., "this entity has direct identifiers like email/phone")
 *   - Used by compliance/auditing tools for GDPR Art.6/Art.9 risk assessment
 *   - Optional, but SHOULD be set for any entity with personal data
 *
 * `pii:*` tags (Entity.tags[])
 *   - String convention: 'pii:direct', 'pii:inferred', etc.
 *   - A marker that an entity carries PII and requires special handling
 *   - Prevents lint rule pii-untagged from firing (signals "yes, I know there's PII here")
 *   - Does NOT replace or conflict with piiCategory — they are complementary
 *
 * Both MAY be present on the same entity. Setting piiCategory without a pii:* tag
 * is valid (and in fact preferred for compliance docs that lack legacy tags).
 * The pii:* tag is primarily for lint hygiene; piiCategory is for classification.
 */
import type { AtlasManifest } from '../types.js';

export interface PiiFinding {
  ref: string;
  message: string;
}

// Word-start match (start of string or after `_`) so camelCase variants like
// `phoneNumber`, `emailAddress`, `dobYear` are still caught.
const PII_FIELD_PATTERN = /(?:^|_)(email|ssn|sin|dob|date_?of_?birth|phone|address|tax_?id|passport)/i;

export function piiUntagged(manifest: AtlasManifest): PiiFinding[] {
  const findings: PiiFinding[] = [];
  const entitiesWithPiiCategory: typeof manifest.entities = [];

  for (const entity of manifest.entities) {
    const hasPiiTag = entity.tags.some((t) => t.startsWith('pii:'));
    if (hasPiiTag) continue;
    for (const field of entity.fields) {
      if (PII_FIELD_PATTERN.test(field.name)) {
        findings.push({
          ref: `${entity.id}.${field.name}`,
          message: `${entity.id}.${field.name} looks like PII (matches /${PII_FIELD_PATTERN.source}/) but ${entity.id} has no pii:* tag`,
        });
      }
    }

    // Track entities with piiCategory for legalBasis compliance check
    if (entity.piiCategory) {
      entitiesWithPiiCategory.push(entity);
    }
  }

  // GDPR compliance note: entities with piiCategory SHOULD specify a legalBasis
  // (e.g., 'consent', 'legitimate_interest', 'legal_obligation').
  // See: https://gdpr-info.eu/art-6-gdpr/
  const missingLegalBasis = entitiesWithPiiCategory.filter((e) => !e.legalBasis);
  if (missingLegalBasis.length > 0) {
    // Non-fatal informational: surfaces the gap for remediation.
    // Remove entries from this list once all entities have legalBasis set.
    console.warn(
      `[atlas-pii] ${missingLegalBasis.length} PII entities missing legalBasis: ` +
        `${missingLegalBasis.map((e) => e.id).join(', ')}. ` +
        `Add 'legalBasis' field to their entity specs (see GDPR Art.6).`,
    );
  }

  return findings;
}
